import { ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { SidebarConfig, WbSidebarConfigContext } from '../../config';
import { useWbSidebarPinState } from '../../WbSidebarPinStateContext';
import { useHomeUrl } from '../../../../hooks';
import { Box, Button, IconButton, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useContent } from '@mainstage/core-components';
import { WbSidebarOpenStateProvider } from '../../WbSidebarOpenStateContext';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { WbSidebarLogo } from '../WbSidebarLogo/WbSidebarLogo';
import { WbSidebarDivider } from '../WbSidebarDivider';
import {
  getFocusVisibleStyle,
  getOnHoverScrollbarStyle,
} from '../../../../styles';

const useStyles = makeStyles<Theme, { sidebarConfig: SidebarConfig }>(
  theme => ({
    ...getOnHoverScrollbarStyle(theme, false),
    root: {
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        display: 'none',
      },
    },
    drawer: {
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      zIndex: theme.zIndex.appBar,
      background: theme.palette.white,
      boxShadow: theme.shadows[23],
    },
    drawerContent: {
      display: 'flex',
      flexFlow: 'column nowrap',
      alignItems: 'flex-start',
      overflowX: 'hidden',
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
      '& > *': {
        flexShrink: 0,
      },
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      '@media print': {
        display: 'none',
      },
      height: '100%',
      borderRight: `1px solid ${theme.palette.bkg.secondary}`,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shortest,
      }),
    },
    drawerWidth: props => ({
      width: props.sidebarConfig.drawerWidthClosed,
    }),
    drawerOpen: props => ({
      width: props.sidebarConfig.drawerWidthOpen,
    }),
    visuallyHidden: {
      top: 0,
      position: 'absolute',
      zIndex: 1000,
      transform: 'translateY(-200%)',
      '&:focus': {
        transform: 'translateY(5px)',
      },
    },
    pinIconWrapper: {
      display: 'inline-flex',
      position: 'absolute',
      top: '80px',
      transform: 'translate(0, -50%)',
      right: 0,
      background: theme.palette.primary.main,
      borderRadius: '4px 0 0 4px',
      justifyContent: 'center',
      alignItems: 'center',
    },
    pinIconButton: {
      '&:focus-visible': {
        ...getFocusVisibleStyle(theme, { inset: false }),
      },
      position: 'absolute',
      top: '50%',
      left: '50%',
      height: 24,
      width: 24,
      transform: 'translate(-50%, -50%)',
      padding: 0,
    },
    pinIcon: {
      color: theme.palette.white,
      fontSize: '16px',
    },
  }),
  { name: 'WbDesktopSidebar' },
);

export type DesktopSidebarProps = {
  openDelayMs?: number;
  closeDelayMs?: number;
  disableExpandOnHover?: boolean;
  children: ReactNode;
};

enum State {
  Closed,
  Idle,
  Open,
}

function A11ySkipSidebar() {
  const { sidebarConfig } = useContext(WbSidebarConfigContext);
  const { focusContent, contentRef } = useContent();
  const classes = useStyles({ sidebarConfig });

  if (!contentRef?.current) {
    return null;
  }
  return (
    <Button
      onClick={focusContent}
      variant="contained"
      className={clsx(classes.visuallyHidden)}
    >
      skipToContent
    </Button>
  );
}

/**
 * Places the Sidebar & wraps the children providing context weather the `Sidebar` is open or not.
 *
 * Handles & delays hover events for expanding the `Sidebar`
 *
 * @param props `disableExpandOnHover` disables the default hover behaviour;
 * `openDelayMs` & `closeDelayMs` set delay until sidebar will open/close on hover
 * @returns
 * @internal
 */
export const WbDesktopSidebar = (props: DesktopSidebarProps) => {
  const { sidebarConfig } = useContext(WbSidebarConfigContext);
  const {
    openDelayMs = sidebarConfig.defaultOpenDelayMs,
    closeDelayMs = sidebarConfig.defaultCloseDelayMs,
    disableExpandOnHover,
    children,
  } = props;

  const classes = useStyles({ sidebarConfig });
  const { isPinned, toggleSidebarPinState } = useWbSidebarPinState();
  const { isTemporarilyPinned } = useWbSidebarPinState();
  const [state, setState] = useState(isPinned ? State.Open : State.Closed);
  const hoverTimerRef = useRef<number>();

  const { homeURL, loading } = useHomeUrl();

  // if a state change is already requested to happen, cancel it
  const cancelPendingStateChange = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = undefined;
    }
  }, []);

  const handleOpen = () => {
    if (disableExpandOnHover) {
      return;
    }
    cancelPendingStateChange();
    if (state !== State.Open) {
      hoverTimerRef.current = window.setTimeout(() => {
        hoverTimerRef.current = undefined;
        setState(State.Open);
      }, openDelayMs);

      setState(State.Idle);
    }
  };

  const handleClose = () => {
    if (disableExpandOnHover) {
      return;
    }
    cancelPendingStateChange();
    if (state === State.Idle) {
      setState(State.Closed);
    } else if (state === State.Open) {
      hoverTimerRef.current = window.setTimeout(() => {
        hoverTimerRef.current = undefined;
        setState(State.Closed);
      }, closeDelayMs);
    }
  };

  const isOpen = state === State.Open || isPinned;

  /**
   * Close/Open Sidebar directly without delays. Also toggles `SidebarPinState` to avoid hidden content behind Sidebar.
   */
  const setPinned = (open: boolean) => {
    if (open) {
      setState(State.Open);
      toggleSidebarPinState();
    } else {
      setState(State.Closed);
      toggleSidebarPinState();
    }
  };

  const onPinButtonClick = () => {
    setPinned(!isPinned);
  };

  // prevent the drawer from accidentally closing when the user hovers on the button
  const handlePinButtonEnter = () => {
    if (disableExpandOnHover) {
      return;
    }
    cancelPendingStateChange();
  };

  const handlePinButtonLeave = () => {
    if (disableExpandOnHover) {
      return;
    }
    handleClose();
  };

  const pinButton = (
    <span className={classes.pinIconWrapper}>
      <IconButton
        className={classes.pinIconButton}
        size="medium"
        data-testid="sidebar-expand-button"
        aria-label={
          isPinned ? 'Unpin navigation sidebar' : 'Pin navigation sidebar'
        }
        onClick={onPinButtonClick}
        onMouseEnter={handlePinButtonEnter}
        onMouseLeave={handlePinButtonLeave}
      />
      {isPinned ? (
        <ChevronLeft className={classes.pinIcon} />
      ) : (
        <ChevronRight className={classes.pinIcon} />
      )}
    </span>
  );

  return (
    <nav className={classes.root} aria-label="sidebar nav">
      <A11ySkipSidebar />
      <WbSidebarOpenStateProvider
        value={{
          isOpen,
          setPinned,
          cancelPendingStateChange,
        }}
      >
        <Box className={classes.root} data-testid="sidebar-root">
          <Box className={clsx(classes.drawer)}>
            <Box
              className={clsx(classes.drawerWidth, classes.drawerContent, {
                [classes.drawerOpen]: isOpen,
              })}
              onMouseEnter={disableExpandOnHover ? () => {} : handleOpen}
              onFocus={disableExpandOnHover ? () => {} : handleOpen}
              onMouseLeave={disableExpandOnHover ? () => {} : handleClose}
              onBlur={disableExpandOnHover ? () => {} : handleClose}
            >
              {!loading && <WbSidebarLogo to={homeURL} />}
              {!isTemporarilyPinned && pinButton}
              <WbSidebarDivider noAboveMargin />
              {children}
            </Box>
          </Box>
        </Box>
      </WbSidebarOpenStateProvider>
    </nav>
  );
};
